import React, { ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import { User, UserManager, UserManagerSettings } from 'oidc-client-ts'
import { AuthContext, IAuthContext } from './AuthContext'
import { Spinner, SpinnerSize, Stack } from '@fluentui/react'

interface AuthProviderProps {
  children: ReactNode
}

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const authority = import.meta.env.VITE_OIDC_AUTHORITY
  const clientId = import.meta.env.VITE_OIDC_CLIENT_ID
  const clientSecret = import.meta.env.VITE_OIDC_CLIENT_SECRET
  const scope = import.meta.env.VITE_OIDC_SCOPE || 'openid profile email'

  const userManager = useMemo(() => {
    const baseUrl = window.location.origin

    const oidcConfig: UserManagerSettings = {
      authority: authority,
      client_id: clientId,
      client_secret: clientSecret,
      redirect_uri: `${baseUrl}/oidc-callback/`,
      silent_redirect_uri: `${baseUrl}/oidc-callback-silent/`,
      post_logout_redirect_uri: `${baseUrl}/logout-callback/`,
      response_type: 'code',
      scope: scope,
      automaticSilentRenew: true,
      filterProtocolClaims: true,
      loadUserInfo: true,
    }

    return new UserManager(oidcConfig)
  }, [authority, clientId, clientSecret, scope])

  useEffect(() => {
    const initAuth = async () => {
      try {
        const user = await userManager.getUser()
        setUser(user)
      } catch (error) {
        console.error('Error initializing auth:', error)
      } finally {
        setIsLoading(false)
      }
    }

    initAuth()

    const handleUserLoaded = (user: User) => setUser(user)
    const handleUserUnloaded = () => setUser(null)
    const handleAccessTokenExpired = async () => {
      try {
        await userManager.signinSilent()
      } catch {
        setUser(null)
      }
    }
    const handleSilentRenewError = (error: Error) => {
      console.error('Silent token renewal failed:', error)
      setUser(null)
    }

    userManager.events.addUserLoaded(handleUserLoaded)
    userManager.events.addUserUnloaded(handleUserUnloaded)
    userManager.events.addAccessTokenExpired(handleAccessTokenExpired)
    userManager.events.addSilentRenewError(handleSilentRenewError)

    return () => {
      userManager.events.removeUserLoaded(handleUserLoaded)
      userManager.events.removeUserUnloaded(handleUserUnloaded)
      userManager.events.removeAccessTokenExpired(handleAccessTokenExpired)
      userManager.events.removeSilentRenewError(handleSilentRenewError)
    }
  }, [userManager])

  const login = async () => {
    await userManager.signinRedirect()
  }

  const logout = async () => {
    await userManager.signoutRedirect()
  }

  const getAccessToken = (): string | null => {
    return user?.access_token || null
  }

  const contextValue: IAuthContext = {
    user,
    isAuthenticated: !!user && !user.expired,
    isLoading,
    login,
    logout,
    getAccessToken,
    userManager,
  }

  if (isLoading) {
    return (
      <Stack verticalAlign="center" horizontalAlign="center" styles={{ root: { height: '100vh' } }}>
        <Spinner size={SpinnerSize.large} label="Checking authentication..." />
      </Stack>
    )
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

export const useAuth = (): IAuthContext => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthProvider
